import React, { useContext, useEffect, useMemo, useState } from "react";
import { DebounceInput } from "react-debounce-input";
import { useRouteMatch, Redirect } from "react-router-dom";

import { SettingsContext } from "../contexts";
import { FilterBy } from "./Orders/index.jsx";
import { PageHeader, PageLayout } from "../components/layout";
import { InfiniteList } from "../components";
import { Button, PageFilterBar, Popover, ScopeSelect, SearchInput, Segmented, Toggle } from "../components/ui";
import { SourcedItem } from "../order/components";
import { FieldGroup } from "../components";
import LeadsGridCard from "../order/components/SourcedItem/LeadsGridCard";
import LeadRow from "../order/components/SourcedItem/LeadRow";

const IInStock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const IInConsignment = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const IAllLeads = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

// Tab bar + page title/subtitle, formerly split across LeadsCategories.jsx
// (the tab row) and RCAdmin/Layout.jsx's PAGE_TITLES/PAGE_SUBTITLES maps (the
// heading). Both were legacy `.rc-topbar` chrome stacked on top of each other
// — same hardcoded dark background and border on both rows, which is exactly
// the boxed-off, separated-from-the-page look Manage Listings doesn't have.
// Now a single shared <PageHeader>, same shape as Manage Listings.
// All Leads first, and the default tab: it is the widest view of the same data,
// so it is where the section should open.
const TAB_DEFS = [
  { type: "leads", label: "All", link: "/leads/leads", icon: <IAllLeads /> },
  { type: "in-stock", label: "In Stock", link: "/leads/in-stock", icon: <IInStock /> },
  { type: "in-consignment", label: "In Consignment", link: "/leads/in-consignment", icon: <IInConsignment /> },
];

// The section leads the title, the tab follows the separator — with the sidebar
// folded, "IN STOCK" alone never said which page you were on.
//
// The separator is an en dash between two EN SPACES (U+2002), not "- ": the H1 is
// uppercase with tight tracking, and a plain hyphen-with-spaces left the two
// halves reading as one word. Typography, not markup, so it survives anywhere the
// title string is reused (tab title, breadcrumb).
const TITLE_SEP = " – ";

const TITLES = {
  "in-stock": `Leads${TITLE_SEP}In Stock`,
  "in-consignment": `Leads${TITLE_SEP}In Consignment`,
  leads: `Leads${TITLE_SEP}All`,
  opportunities: `Leads${TITLE_SEP}Opportunities`,
};

// Figma node 421-5630. In Consignment and All Leads have no subtitle in any
// frame seen so far — left absent rather than invented.
const SUBTITLES = {
  "in-stock": "Leads that match your inventory (potential sale).",
};

const sortOptions = [
  { id: null, name: "Request Date" },
  { id: "week", name: "Most Requested (Week)" },
  { id: "last_month", name: "Most Requested (Month)" },
  { id: "last_three_month", name: "Most Requested (3 Months)" },
];

// Region is one `region` enum on the API (GET /api/requests/leads/*), so the
// dropdown runs in ScopeSelect's single mode — checkbox rows, exclusive choice.
// `null` ("All") is the absence of a selection here, so it is not an option row.
const regionScopeOptions = [
  { key: "uk", label: "UK" },
  { key: "us", label: "US" },
  { key: "europe", label: "Europe" },
  { key: "middle-east", label: "Middle East" },
  { key: "asia", label: "Asia" },
  { key: "australia", label: "Australia" },
  { key: "north-america", label: "North America" },
  { key: "south-america", label: "South America" },
  { key: "africa", label: "Africa" },
];

const GRID_TABS = ["leads", "in-stock", "in-consignment"];

// Cards read one enquiry at a time; the list runs down a filtered set. Both are
// useful at different moments, so the choice is the user's and it sticks.
const VIEW_STORAGE_KEY = "rc.leads.view";
// Icon-only on the row — the two shapes say it faster than the words, and the
// labels stay in the DOM as the controls' accessible names (hidden in CSS, see
// `.leads-viewswitch` in _leads-dark-theme.scss). "Grid", not "Cards": it is the
// grid of cards, and that is what the rest of the app calls it.
// The stored values stay `cards` / `list` so an existing preference still reads.
const VIEW_OPTIONS = [
  {
    value: "cards",
    label: "Grid",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
        <rect x="9" y="1.5" width="5.5" height="5.5" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
        <rect x="1.5" y="9" width="5.5" height="5.5" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
        <rect x="9" y="9" width="5.5" height="5.5" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      </svg>
    ),
  },
  {
    value: "list",
    label: "List",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <line x1="5.5" y1="3.5" x2="14.5" y2="3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        <line x1="5.5" y1="8" x2="14.5" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        <line x1="5.5" y1="12.5" x2="14.5" y2="12.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        <circle cx="2.2" cy="3.5" r="1" fill="currentColor" />
        <circle cx="2.2" cy="8" r="1" fill="currentColor" />
        <circle cx="2.2" cy="12.5" r="1" fill="currentColor" />
      </svg>
    ),
  },
];

const readStoredView = () => {
  try {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return stored === "list" || stored === "cards" ? stored : "cards";
  } catch {
    // Private windows and blocked site data throw on access, not on read.
    return "cards";
  }
};

// Every `:orderLeadType` the page knows how to render. `alerts` takes the
// separate Buy Alerts branch at the bottom of this component; `opportunities`
// is reachable from the RC sidebar (layouts/RC/Sidebar.jsx) and resolves to the
// opportunities endpoint, though it is deliberately NOT in GRID_TABS — see the
// note on getFetchUrl below.
//
// Without this list any string routed here — /leads/foo — rendered the page and
// fired GET /api/requests/leads/foo at the API.
const VALID_LEAD_TYPES = [...GRID_TABS, "opportunities", "alerts"];

const DEFAULT_LEAD_TYPE = "leads";

const getFetchUrl = (type) => {
  if (type === "in-stock" || type === "in-consignment") {
    return "/api/requests/leads/opportunities";
  }
  return `/api/requests/leads/${type}`;
};

const getIsStock = (type) => {
  if (type === "in-stock") return true;
  if (type === "in-consignment") return false;
  return false;
};

const Leads = () => {
  const match = useRouteMatch();
  const settings = useContext(SettingsContext);
  const [period, setPeriod] = useState(null);
  const [search, setSearch] = useState("");
  const [brandId, setBrandId] = useState(null);
  const [modelId, setModelId] = useState(null);
  const categoryId = 9;
  const [region, setRegion] = useState(null);
  const [smartMatch, setSmartMatch] = useState(false);

  const type = match?.params?.orderLeadType;
  const isGridView = GRID_TABS.includes(type);

  const [view, setView] = useState(readStoredView);
  const onViewChange = (next) => {
    setView(next);
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, next); } catch { /* storage unavailable */ }
  };
  const isListView = isGridView && view === "list";
  const isValidType = VALID_LEAD_TYPES.includes(type);

  const tabs = useMemo(
    () =>
      TAB_DEFS.map((tab) => ({
        label: tab.label,
        to: `${tab.link}?${settings?.iframeQueryParams ?? ""}`,
        icon: tab.icon,
        match: (path) => path === tab.link,
      })),
    [settings?.iframeQueryParams]
  );

  const filters = useMemo(
    () => ({
      period,
      search,
      brand_id: brandId,
      model_id: modelId,
      is_stock: getIsStock(type),
      region,
    }),
    [period, search, brandId, modelId, type, region]
  );

  useEffect(() => {
    setSearch("");
    setPeriod(null);
    setBrandId(null);
    setModelId(null);
    setRegion(null);
  }, [type]);

  // Placed after every hook so the hook order stays stable — React forbids an
  // early return above them. An unrecognised :orderLeadType now lands on the
  // default tab instead of rendering an empty page and requesting a
  // nonexistent endpoint.
  if (!isValidType) {
    return <Redirect to={`/leads/${DEFAULT_LEAD_TYPE}`} />;
  }

  return (
    <PageLayout className={type !== "alerts" ? "leads-dark" : ""}>
      {type !== "alerts" && (
        <PageHeader
          title={TITLES[type] || "Leads"}
          description={SUBTITLES[type]}
          tabs={tabs}
        />
      )}
      {type !== "alerts" && (
        <main id="pages" className="flex-grow-1 mb-5">
          {/* Fontaine M54C — same shape as Manage Listings' filter row
              (RCListingsPage): search pill on the left, then the scope
              dropdown, the Filter panel and Reset pinned right. Every control
              keeps its exact handler, payload and endpoint from the FiltersBar
              row it replaces — see tests/views/LeadsToolbarContract.test.jsx. */}
          <PageFilterBar
            className="leads-filterbar"
            label="Filter leads"
            search={(
              <DebounceInput
                element={SearchInput}
                debounceTimeout={300}
                forceNotifyByEnter={false}
                size="lg"
                name="search"
                autoComplete="off"
                aria-label="Search leads"
                placeholder="Quick search for products"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
          >
              <ScopeSelect
                label="Region"
                placeholder="All regions"
                multiple={false}
                options={regionScopeOptions}
                value={region}
                onChange={setRegion}
              />

              {/* Brand, Model and Request Date, previously inline on the row —
                  now behind one ghost trigger, as on Manage Listings. */}
              <Popover
                label="Filter leads"
                className="leads-filterbar__pop"
                side="bottom"
                align="end"
                trapFocus
                trigger={(
                  <Button
                    type="button"
                    variant={brandId || modelId || period ? "secondary" : "ghost"}
                    size="lg"
                    aria-label="Filter by"
                    leadingIcon={(
                      <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                        <line x1="2" y1="5" x2="14" y2="5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                        <line x1="4" y1="9" x2="12" y2="9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                        <line x1="6" y1="13" x2="10" y2="13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                      </svg>
                    )}
                  >
                    Filter
                  </Button>
                )}
              >
                <div className="leads-filterbar__panel">
                  <FieldGroup
                    placeholder="Select Brand"
                    name="brand_id"
                    type="select"
                    optionsUrl={`/api/categories/${categoryId}/brands`}
                    prependStaticOptions={[{ id: null, name: "All" }]}
                    onChange={(b) => { setBrandId(b); setModelId(null); }}
                    noFormik
                    value={brandId}
                  />
                  {brandId && (
                    <FieldGroup
                      placeholder="Select Model"
                      name="model_id"
                      type="select"
                      optionsUrl={`/api/categories/${categoryId}/brands/${brandId}/models`}
                      prependStaticOptions={[{ id: null, name: "All Models" }]}
                      onChange={setModelId}
                      noFormik
                      value={modelId}
                    />
                  )}
                  <FilterBy
                    placeholder="Request Date"
                    filterBy={period}
                    setFilterBy={setPeriod}
                    options={sortOptions}
                  />
                </div>
              </Popover>

              {/* Same clear-all as before. */}
              <Button
                variant="ghost"
                size="lg"
                onClick={() => {
                  setBrandId(null);
                  setModelId(null);
                  setRegion(null);
                  setPeriod(null);
                }}
              >
                Reset
              </Button>

          </PageFilterBar>

            {/* Own line, under the filters. Neither of these narrows the result
                set — Smart Match changes how matches are scored and the switch
                changes how they are drawn — so the filter row above is left to
                Region, Filter and Reset alone. */}
            <div className="leads-viewrow">
              {/* "Match" with a raised purple AI, so the mark reads as one word
                  and the AI part carries the accent. `aria-label` keeps it a
                  single spoken name rather than "Match A I". */}
              <Toggle
                size="lg"
                aria-label="Match AI"
                label={<>Match<sup className="leads-ai">AI</sup></>}
                checked={smartMatch}
                onChange={setSmartMatch}
              />

              {isGridView && (
                <Segmented
                  size="lg"
                  className="leads-viewswitch"
                  label="Leads view"
                  value={view}
                  onChange={onViewChange}
                  options={VIEW_OPTIONS}
                />
              )}
            </div>

            {/* Was `${isMobile ? "mx-2" : ""}` via react-device-detect. That is
                UA sniffing, not a viewport query: it disagreed with the CSS
                breakpoints elsewhere in this page and never re-evaluated on
                resize or rotation. The inset is now a media query on
                .leads-results, matching src/styles/breakpoints. */}
            <section className="orders leads-results mt-3">
              <InfiniteList
                className={isListView ? "lead-list" : isGridView ? "leads-grid" : "orders"}
                fetchUrl={getFetchUrl(type)}
                fetchParams={{ filters }}
                ItemComponent={isListView ? LeadRow : isGridView ? LeadsGridCard : SourcedItem}
                isFromUsersPage
                isLeadsPage
                key={type}
                type={type}
              />
            </section>
        </main>
      )}
      {/* /leads/alerts predates the standalone /alerts page (RCBuyAlertsPage) and
          rendered the legacy, un-themed Alerts list inside this RC dark shell.
          Nothing in the app links here anymore (Sidebar.jsx / Menu.jsx both
          point at /alerts) — redirect rather than keep a second, unstyled
          "manage alerts" surface reachable by direct URL. */}
      {type == "alerts" && <Redirect to="/alerts" />}
    </PageLayout>
  );
};

export default Leads;
